import { useContext, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { ExternalMedia, Post as PostT, Source } from '@sodamnfoolish/sjc-backend-types/src/api/feed/dto';
import { format } from 'date-fns';

import ExternalLink from '../../Components/assets/external-link.svg';
import { getSourceDetails, SourceDetails } from '../../utils/source-constants';
import { DeployBlockTimer, Timer } from '../../Components/DeployBlockTimer';
import * as Virtual from '../../Components/VirtualGrid/VirtualGrid';
import { AdvancedFeedHeader } from '../../Components/posts/AdvancedFeedHeader';
import { ArrowRightIcon } from '../../Components/icons/ArrowRightIcon';
import { TwitterIcon } from '../../Components/icons/TwitterIcon';
import { RetweetIcon } from '../../Components/icons/RetweetIcon';
import { TweetReplyIcon } from '../../Components/icons/TweetReplyIcon';

import { FeedContext } from './context/FeedContext';
import { SoundStoreContext } from './context/SoundStoreContext';
import { TweetTypesContext } from './context/TweetTypesContext';
import { BonkLinkedContext, PumpFunLinkedContext } from './context/LinkedContext';

import '../../utils/gradients.css';
import { useLatestCallbackRef } from '../../Hooks/useLatestCallbackRef';

export const EveryoneColumn = Virtual.createColumn<{ onCreate: (post: PostT, details: SourceDetails) => void }>(({ id, width, onCreate }) => {
    const feed = useContext(FeedContext);

    return (
        <Virtual.Column id={id} width={width}>
            <Virtual.Title>
                <Virtual.ColumnHeader key='Everyone'>
                    <Timer deployBlockTime={0} timers={[]}>
                        {(timestampStr) => <DateTime className='h-full flex justify-center items-center text-[12px]' date={Number(timestampStr)} />}
                    </Timer>
                </Virtual.ColumnHeader>
            </Virtual.Title>
            {feed.Everyone.length === 0 ? (
                <Virtual.Block id='Everyone loading'>
                    <h3 className='flex justify-center'>Loading...</h3>
                </Virtual.Block>
            ) : (
                feed.Everyone.map((post) => (
                    <Virtual.Block id={post.id}>
                        <Post
                            key={post.id}
                            post={post}
                            createButtonPosition='footer'
                            renderHeader={(post, stroke, image, formattedElapsedTime) => (
                                <div className='flex items-center'>
                                    <img className={'flex w-6 h-6 mr-[10px] border-[2px] rounded-[50%] ' + stroke} src={image} alt='avatar' />
                                    <span className='flex-1 font-bold'>{post.username}</span>
                                    <span className='mr-[14px]'>{formattedElapsedTime}</span>
                                    {post?.tweetType === 'tweet' && <TwitterIcon className='size-[24px] opacity-49 filter invert' />}
                                    {post?.tweetType === 'retweet' && <RetweetIcon className='size-[24px] opacity-49 filter invert' />}
                                    {post?.tweetType === 'reply' && <TweetReplyIcon className='size-[24px] opacity-49 filter invert' />}
                                </div>
                            )}
                            renderFooter={(post) => (
                                <div className='flex items-center'>
                                    <a
                                        href={post.url ? post.url : '#'}
                                        target='_blank'
                                        rel='noreferrer'
                                        className='size-[24px] mr-[10px] cursor-pointer'
                                        onClick={(e) => {
                                            if (!post.url) {
                                                e.preventDefault();
                                                console.log('URL not provided');
                                            }
                                        }}
                                    >
                                        <img src={ExternalLink} alt='External Link' />
                                    </a>
                                    <DateTime className='text-[10px]' date={post.date} />
                                </div>
                            )}
                            onCreate={onCreate}
                        />
                    </Virtual.Block>
                ))
            )}
        </Virtual.Column>
    );
});

export const SourceColumn = Virtual.createColumn<{ source: Source; onCreate: (post: PostT, details: SourceDetails) => void }>(
    ({ id, width, source, onCreate }) => {
        const feed = useContext(FeedContext);

        const { image, stroke, color, url } = getSourceDetails(source);

        const soundStore = useContext(SoundStoreContext);
        const soundStatus = useSyncExternalStore(soundStore.subscribe, soundStore.get);

        const tweetTypesStore = useContext(TweetTypesContext);
        const tweetTypes = useSyncExternalStore(tweetTypesStore.subscribe, tweetTypesStore.get);

        const sourceTweetTypes = tweetTypes[source.username];

        return (
            <Virtual.Column id={id} width={width}>
                <Virtual.Title>
                    <Virtual.ColumnHeader key={source.username} className='flex items-center justify-between text-[12px] font-normal p-[4px]'>
                        <img className={'size-[28px] mr-[10px] border-[2px] rounded-[50%] ' + stroke} src={`data:image/jpeg;base64,${image}`} alt='avatar' />
                        <span className='flex-1 font-bold'>{source.username}</span>
                        <AdvancedFeedHeader
                            image={image}
                            color={color}
                            url={url}
                            stroke={stroke}
                            source={source}
                            soundStatus={soundStatus}
                            soundTweetTypes={sourceTweetTypes?.soundTweetTypes}
                            selectedTweetTypes={sourceTweetTypes?.selectedTweetTypes}
                            onSoundStatusChange={(state) => {
                                soundStore.set(state);
                                localStorage.setItem('soundsEnabled', JSON.stringify(state));
                            }}
                            onTweetTypesUpdate={(username, selectedTweetTypes, soundTweetTypes) => {
                                tweetTypesStore.set({
                                    ...tweetTypes,
                                    [source.username]: {
                                        selectedTweetTypes: selectedTweetTypes!,
                                        soundTweetTypes: soundTweetTypes!,
                                    },
                                });
                                localStorage.setItem(`selectedTweetTypes_${username}`, JSON.stringify(Array.from(selectedTweetTypes!)));
                                localStorage.setItem(`soundTweetTypes_${username}`, JSON.stringify(Array.from(soundTweetTypes!)));
                            }}
                        />
                    </Virtual.ColumnHeader>
                </Virtual.Title>
                {!feed[source.username] ? (
                    <Virtual.Block key={`${source.username} Loading`} id={`${source.username} Loading`}>
                        <h3 className='flex justify-center'>Loading...</h3>
                    </Virtual.Block>
                ) : (
                    feed[source.username].map((post) => (
                        <Virtual.Block key={post.id} id={post.id}>
                            <Post
                                key={post.id}
                                post={post}
                                createButtonPosition='footer'
                                renderFooter={(post, _, __, formattedElapsedTime) => (
                                    <div className='flex items-center'>
                                        {post?.tweetType === 'tweet' && <TwitterIcon className='size-[24px] opacity-49 filter invert mr-[10px]' />}
                                        {post?.tweetType === 'retweet' && <RetweetIcon className='size-[24px] opacity-49 filter invert mr-[10px]' />}
                                        {post?.tweetType === 'reply' && <TweetReplyIcon className='size-[24px] opacity-49 filter invert mr-[10px]' />}
                                        <span className='flex-1'>{formattedElapsedTime}</span>
                                        <DateTime className='text-[10px]' date={post.date} />
                                    </div>
                                )}
                                onCreate={onCreate}
                            />
                        </Virtual.Block>
                    ))
                )}
            </Virtual.Column>
        );
    },
);

export function Post({
    post,
    createButtonPosition = 'footer',

    renderHeader,
    renderFooter,

    onCreate,
}: {
    post: PostT;
    createButtonPosition?: 'body' | 'footer';

    renderHeader?: (post: PostT, stroke: string, image: string, elapsedTime: string) => React.ReactElement;
    renderFooter?: (post: PostT, stroke: string, image: string, elapsedTime: string) => React.ReactElement;

    onCreate?: (post: PostT, details: SourceDetails) => void;
}) {
    const details = getSourceDetails(post);
    const { image, stroke } = details;

    const deployBlockTime = Math.floor(new Date(post.date).valueOf() / 1000);

    const onCreateRef = useLatestCallbackRef((post: PostT) => {
        onCreate?.(post, details);
    });

    const markup = useMemo(
        () => (
            <>
                {renderHeader && (
                    <DeployBlockTimer deployBlockTime={deployBlockTime ?? 0} timers={[]}>
                        {(formattedElapsedTime) => renderHeader(post, stroke, `data:image/jpeg;base64,${image}`, formattedElapsedTime)}
                    </DeployBlockTimer>
                )}
                <div className='flex items-center'>
                    <div className='flex flex-1 flex-col'>
                        <span className='mb-[11px] text-[10px]'>{post.text}</span>
                        {post.media.map((media, index) => (
                            <img key={index} src={(media as ExternalMedia).url} alt='Post Img' />
                        ))}
                    </div>
                    {createButtonPosition === 'body' && (
                        <button
                            className='size-[24px] ml-[14px]'
                            onClick={() => {
                                onCreateRef.current(post);
                            }}
                        >
                            <ArrowRightIcon className='w-full h-full cursor-pointer' />
                        </button>
                    )}
                </div>
                {renderFooter && (
                    <div className='flex items-center'>
                        <div className='flex-1'>
                            <DeployBlockTimer deployBlockTime={deployBlockTime ?? 0} timers={[]}>
                                {(formattedElapsedTime) => renderFooter(post, stroke, `data:image/jpeg;base64,${image}`, formattedElapsedTime)}
                            </DeployBlockTimer>
                        </div>
                        {createButtonPosition === 'footer' && (
                            <button
                                className='size-[24px] ml-[14px]'
                                onClick={() => {
                                    onCreateRef.current(post);
                                }}
                            >
                                <ArrowRightIcon className='w-full h-full cursor-pointer' />
                            </button>
                        )}
                    </div>
                )}
            </>
        ),
        [createButtonPosition, deployBlockTime, image, onCreateRef, post, renderFooter, renderHeader, stroke],
    );

    return <Container post={post}>{markup}</Container>;
}

function Container({ post, children }: React.PropsWithChildren<{ post: PostT }>) {
    const pumpfunlinkedMap = useContext(PumpFunLinkedContext);
    const bonklinkedMap = useContext(BonkLinkedContext);

    const { color, stroke } = getSourceDetails(post);

    const linked = pumpfunlinkedMap[post.pumpFunBubble?.id ?? -1] || bonklinkedMap[post.letsBonkBubble?.id ?? -1];

    const [highlighted, setHighlighted] = useState(false);

    useEffect(() => {
        onHightlightPostEvent((highligtedPost) => {
            if (post.id !== highligtedPost.id) return;

            setHighlighted(true);

            setTimeout(() => {
                setHighlighted(false);
            }, 60000);
        });
    }, [post, post.id]);

    return (
        <div
            className={`
                flex flex-col gap-[11px] p-[9px] text-[12px] font-normal ${color}
                ${highlighted ? 'border-[3px] border-yellow-400 animate-[highlight_1.5s_ease-in-out_infinite_alternate]' : ''}
                ${linked ? 'border-[3px] ' + stroke : ''}
            `}
        >
            {children}
        </div>
    );
}

export function DateTime({ className = '', date }: { date: string | number; className?: string }) {
    return (
        <span className={className}>
            <span className='font-bold'>{format(date, 'H:mm:ss ')}</span> - <span className='font-normal'>{format(date, 'd/M/yy')}</span>
        </span>
    );
}

export function dispatchHighlightPost(post: PostT) {
    window.dispatchEvent(new HightlightPostEvent(post));
}

export function onHightlightPostEvent(callback: (post: PostT) => void) {
    const handler = (event: HightlightPostEvent) => {
        console.log('EVENtS');
        callback(event.detail);
    };

    window.addEventListener(hightlightPostEvent, handler);

    return () => {
        window.removeEventListener(hightlightPostEvent, handler);
    };
}

const hightlightPostEvent: 'HightlightPost' = 'HightlightPost';

class HightlightPostEvent extends CustomEvent<PostT> {
    constructor(detail: PostT) {
        super(hightlightPostEvent, { detail });
    }
}

declare global {
    interface WindowEventMap {
        [hightlightPostEvent]: HightlightPostEvent;
    }
}
